import { motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import axios from 'axios';
import Button from './reusable/Button';
import { Bounce, toast } from 'react-toastify';

const selectOptions = ['Web Application', 'UI/UX Design'];

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters long'),
  email: z.string().email('Invalid email address'),
  subject: z.enum(['Web Application', 'UI/UX Design']),
  message: z.string().min(10, 'Message must be at least 10 characters long'),
});

const HireMeModal = ({ onClose }) => {
  //State management
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const SubmittionSubmitted = () => {
    toast('Email sent successfully. I will reach out shortly', {
      position: 'top-right',
      autoClose: 2000,
      hideProgressBar: false,
      theme: 'dark',
      closeOnClick: true,
      pauseOnHover: false,
      transition: Bounce,
    });
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('access_key', '8e41be63-9d7e-442c-86c6-f7e717bd1e6c');
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('subject', data.subject);
      formData.append('message', data.message);

      const response = await axios.post(
        'https://api.web3forms.com/submit',
        formData
      );

      if (response.data.success) {
        SubmittionSubmitted();
        setSuccess(true);

        reset();
        setTimeout(onClose, 3000);
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="font-general-medium fixed inset-0 z-30 transition-all duration-500"
    >
      <main className="flex flex-col items-center justify-center h-full w-full min-h-screen">
        <div className="modal-wrapper flex items-center z-30">
          <div className="modal max-w-md mx-5 xl:max-w-xl lg:max-w-xl md:max-w-xl bg-secondary-light dark:bg-primary-dark max-h-screen shadow-lg flex-row rounded-lg relative">
            <div className="modal-header flex justify-between gap-10 p-5 border-b border-ternary-light dark:border-ternary-dark">
              <h5 className=" text-primary-dark dark:text-primary-light text-xl">
                What idea do you want to bring up to life?
              </h5>
              <button
                onClick={onClose}
                className="px-4 font-bold text-primary-dark dark:text-primary-light"
              >
                <FiX className="text-3xl" />
              </button>
            </div>
            <div className="modal-body p-5 w-full h-full">
              {/* Body */}
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="max-w-xl m-4 text-left"
              >
                <div>
                  <input
                    {...register('name')}
                    className="w-full px-5 py-2 border dark:border-secondary-dark rounded-md text-md bg-secondary-light dark:bg-ternary-dark text-primary-dark dark:text-ternary-light"
                    placeholder="John Doe"
                    aria-label="Name"
                  />
                  {errors.name && (
                    <p className="text-primary-error text-sm">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div className="mt-6">
                  <input
                    {...register('email')}
                    className="w-full px-5 py-2 border dark:border-secondary-dark rounded-md text-md bg-secondary-light dark:bg-ternary-dark text-primary-dark dark:text-ternary-light"
                    placeholder="you@example.com"
                    aria-label="Email"
                  />
                  {errors.email && (
                    <p className="text-primary-error text-sm text-sm">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="mt-6">
                  <select
                    {...register('subject')}
                    className="w-full px-5 py-2 border dark:border-secondary-dark rounded-md text-md bg-secondary-light dark:bg-ternary-dark text-primary-dark dark:text-ternary-light"
                    aria-label="Project Category"
                  >
                    {selectOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {errors.subject && (
                    <p className="text-primary-error text-sm text-sm">
                      {errors.subject.message}
                    </p>
                  )}
                </div>

                <div className="mt-6">
                  <textarea
                    {...register('message')}
                    className="w-full px-5 py-2 border dark:border-secondary-dark rounded-md text-md bg-secondary-light dark:bg-ternary-dark text-primary-dark dark:text-ternary-light"
                    cols="14"
                    rows="6"
                    aria-label="Details"
                    placeholder="Project description"
                  ></textarea>
                  {errors.message && (
                    <p className="text-primary-error  text-sm">
                      {errors.message.message}
                    </p>
                  )}
                </div>
                <div className="mt-6 pb-4 sm:pb-1">
                  <button
                    type="submit"
                    className="px-4 sm:px-6 py-2 sm:py-2.5 text-white bg-indigo-500 hover:bg-indigo-600 rounded-md focus:ring-1 focus:ring-indigo-900 duration-500"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Request'}
                  </button>
                </div>
              </form>
            </div>
            <div className="modal-footer mt-2 sm:mt-0 py-5 px-8 text-right">
              <button
                onClick={onClose}
                type="button"
                className="px-4 sm:px-6 py-2 bg-gray-600 text-primary-light hover:bg-ternary-dark dark:bg-gray-200 dark:text-secondary-dark dark:hover:bg-primary-light rounded-md focus:ring-1 focus:ring-indigo-900 duration-500"
              >
                <Button title="Close" />
              </button>
            </div>
          </div>
        </div>
      </main>
    </motion.div>
  );
};

export default HireMeModal;
